// Central place for network + contract configuration. Everything here can
// be overridden with Netlify environment variables (see .env.example)
// without touching code — e.g. flip VITE_WAX_NETWORK from "testnet" to
// "mainnet" once you're ready to go live.

export const network = import.meta.env.VITE_WAX_NETWORK || "testnet";

export const chain = {
  id:
    import.meta.env.VITE_WAX_CHAIN_ID ||
    // Confirmed against docs.wax.io's testnet block-producer setup guide and
    // an EOSphere WAX testnet walkthrough (both independently agree on this
    // exact 64-char value) after a prior version of this file had it one
    // character short — a truncated chain ID would have made WharfKit refuse
    // to sign against testnet entirely.
    "f16b1833c747c43682f4386fca9cbb327929334a762755ebec17f6f23c9b8a12",
  url: import.meta.env.VITE_WAX_RPC_URL || "https://waxtestnet.greymass.com",
};

export const atomicApiUrl =
  import.meta.env.VITE_ATOMIC_API_URL || "https://test.wax.api.atomicassets.io";

export const contracts = {
  atomicassets: import.meta.env.VITE_ATOMICASSETS_CONTRACT || "atomicassets",
  atomicmarket: import.meta.env.VITE_ATOMICMARKET_CONTRACT || "atomicmarket",
  token: import.meta.env.VITE_TOKEN_CONTRACT || "eosio.token",
  // Not deployed yet — see the escrow contract build task. Mint/redeem-new
  // flows will fail with "account does not exist" until this is live.
  escrow: import.meta.env.VITE_ESCROW_CONTRACT || "escrowbacker",
  // Also not deployed yet. Handles both plain (unbacked) and backed drops
  // — see the nftdrops-contract project.
  nftdrops: import.meta.env.VITE_NFTDROPS_CONTRACT || "nftdrops",
  // On-chain order book (orderbook-contract/orderbook.cpp). Needs its own
  // dedicated account — never milkpuddings/octoberlists, since setcode
  // replaces whatever contract is already there.
  orderbook: import.meta.env.VITE_ORDERBOOK_CONTRACT || "waxcreatorex",
};

export const marketplaceName = import.meta.env.VITE_MARKETPLACE_NAME || "";

export const appName = "WAXCreator";

// The fixed schema every "Create NFT" one-step wizard mint uses, so the
// wizard never has to ask a first-time creator to design a schema
// themselves. `attributes` holds a single JSON-encoded string of whatever
// custom key/value pairs the creator entered — AtomicAssets' schema format
// is fixed at creation time and doesn't support nested/arbitrary fields
// directly, so a JSON blob in one string field is the standard workaround.
// Creators who want a "real" multi-field schema (e.g. reused across many
// templates with per-field typing/filtering on AtomicHub) should use the
// Advanced Create page instead, which lets them design their own.
export const WAXCREATOR_SCHEMA_NAME = "waxcreator";
export const WAXCREATOR_SCHEMA_FORMAT = [
  { name: "name", type: "string" },
  { name: "img", type: "image" },
  { name: "description", type: "string" },
  { name: "attributes", type: "string" },
];

// Symbols this marketplace treats as valid "backing" tokens. Extend if you
// support more than WAX/EOS. `precision` matches each token's on-chain
// decimal places (WAX = 8, EOS = 4).
export const backingTokens: Record<string, { precision: number; contract: string }> = {
  WAX: { precision: 8, contract: contracts.token },
  EOS: { precision: 4, contract: "eosio.token" },
};

export const isMainnet = network === "mainnet";

// WAX accounts allowed to open /admin, comma-separated (e.g.
// "milkpuddings,someotheraccount"). Defaults to the account that deployed
// escrowbacker — override via Netlify env var if you want more/different
// accounts to have access. This is a client-side, wallet-signature-backed
// check (see pages/Admin.tsx) — it hides the panel from everyone else, but
// remember none of the data behind it is private: it's all either public
// chain state or this site's own best-effort activity log, never anything
// like a private key or personal info.
export const adminAccounts = (import.meta.env.VITE_ADMIN_ACCOUNTS || "milkpuddings")
  .split(",")
  .map((a: string) => a.trim())
  .filter(Boolean);
